import {Button, DatePicker, Form, Select} from 'antd'
import {ReportRequestParametersObjectObject, SavedReportBean} from 'common/api/api.ts'
import {AnchorsForm} from 'common/components/AnchorsForm'
import {EventReportSelector} from "common/components/dictionary/EventReportSelector.tsx";
import {TimeZoneSelector} from 'common/components/dictionary/TimeZoneSelector.tsx'
import {FormSortableSelect} from 'common/components/formSortableSelect/FormSortableSelect.tsx'
import React from 'react'

import 'common/components/formSortableSelect/formSortableSelect.css'

import {ReportFiltersComponent} from './ReportFiltersComponent.tsx'
import {FilterKV} from './types.ts'

const {RangePicker} = DatePicker

export interface ReportFormProps {
    loading: boolean
    periodLabels: Record<string, string>
    onGenerate: () => void
    allFilters?: any[]
    selectedFilters: FilterKV[]
    setSelectedFilters: (x: FilterKV[]) => void
    selectedKeyColumns: string[]
    setSelectedKeyColumns: (x: string[]) => void
    selectedValueColumns: string[]
    setSelectedValueColumns: (x: string[]) => void
    selectedEventColumns: string[]
    setSelectedEventColumns: (x: string[]) => void
    reportKeys: any[]
    reportValues: any[]
    form: any
    reportType?: ReportRequestParametersObjectObject.TypeEnum
}

export const ReportForm: React.FC<ReportFormProps> = ({
                                                          loading,
                                                          periodLabels,
                                                          onGenerate,
                                                          allFilters,
                                                          selectedFilters,
                                                          setSelectedFilters,
                                                          selectedKeyColumns,
                                                          setSelectedKeyColumns,
                                                          selectedValueColumns,
                                                          setSelectedValueColumns,
                                                          selectedEventColumns,
                                                          setSelectedEventColumns,
                                                          reportKeys,
                                                          reportValues,
                                                          form,
                                                          reportType
                                                      }) => {
    const periodOptions = [
        SavedReportBean.ReportPeriodEnum.TODAY,
        SavedReportBean.ReportPeriodEnum.YESTERDAY,
        SavedReportBean.ReportPeriodEnum.LAST3DAYS,
        SavedReportBean.ReportPeriodEnum.LAST7DAYS,
        SavedReportBean.ReportPeriodEnum.LAST28DAYS,
        SavedReportBean.ReportPeriodEnum.THISMONTH,
        SavedReportBean.ReportPeriodEnum.LASTMONTH
    ]

    const isTrackerReport = reportType === ReportRequestParametersObjectObject.TypeEnum.Tracker
    const periodTooltip = isTrackerReport
        ? 'Select the date range for which tracker event data should be displayed.'
        : 'Select the time range for the data you want to include in the report.'
    const dimensionsLabel = isTrackerReport ? 'Columns' : 'Dimensions'
    const dimensionsTooltip = isTrackerReport
        ? 'Choose which data fields to show in the report (e.g., Time, Tracker, SSP, Ad Unit, Country, User IP, Event).'
        : 'Select how the data will be displayed in the report (for example: campaign, country, device).'
    const dimensionsPlaceholder = isTrackerReport ? 'Select Columns' : 'Select Dimensions'
    const filtersTooltip = isTrackerReport
        ? 'Apply conditions to narrow down the event data based on specific criteria.'
        : 'Use filters to limit the data included in the report. Choose the dimension you want to filter by.'
    const dimensionsOptions = reportKeys.map((column) => ({
        label: column.title,
        value: column.value
    }))

    const valuesOptions = reportValues.map((column) => ({
        label: column.title,
        value: column.value || column.title
    }))

    return (
        <AnchorsForm
            form={form}
            onFinish={onGenerate}
        >
            <Form.Item label="Period" tooltip={periodTooltip} required>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                        gap: 12,
                    }}
                >
                    <Form.Item
                        name="period"
                        rules={[{required: true, message: 'Please select period'}]}
                        style={{marginBottom: 0}}
                    >
                        <Select
                            className="subtle-placeholder"
                            placeholder="Range"
                            options={periodOptions.map((value) => ({
                                label: periodLabels[value],
                                value
                            }))}
                            labelRender={({label, value}) => periodLabels[String(value)] ?? label}
                            style={{color: '#0D1A43'}}
                            aria-label="Select period"
                        />
                    </Form.Item>

                    <Form.Item
                        name="dateRange"
                        rules={[{required: true, message: 'Please select date range'}]}
                        style={{marginBottom: 0}}
                    >
                        <RangePicker
                            className="subtle-placeholder"
                            style={{width: '100%'}}
                            allowClear={false}
                            onChange={(dates) => {
                                if (dates?.[0] && dates[1]) {
                                    form.setFieldValue('period', SavedReportBean.ReportPeriodEnum.CUSTOMRANGE)
                                }
                            }}
                            aria-label="Select report date range"
                        />
                    </Form.Item>
                </div>
            </Form.Item>

            <Form.Item
                label="Timezone"
                tooltip="Choose the timezone used to calculate and display report data."
                name="timezone"
                rules={[{required: true}]}
            >
                <TimeZoneSelector/>
            </Form.Item>

            <Form.Item
                label={dimensionsLabel}
                tooltip={dimensionsTooltip}
                name="dimensions"
                rules={
                    isTrackerReport ? [{required: true, message: 'Please select at least one dimension'}] : undefined
                }
            >
                <FormSortableSelect
                    value={selectedKeyColumns}
                    onChange={setSelectedKeyColumns}
                    placeholder={dimensionsPlaceholder}
                    options={dimensionsOptions}
                    required={false}
                    selectProps={{showSearch: true}}
                />
            </Form.Item>

            {!isTrackerReport && (
                <>
                    <Form.Item
                        label="Values"
                        tooltip="Choose the metrics you want to measure in the report (such as bids, CTR, or CVR)."
                        name="metrics"
                        required
                        rules={[{required: true, message: 'Please select at least one value'}]}
                    >
                        <FormSortableSelect
                            value={selectedValueColumns}
                            onChange={setSelectedValueColumns}
                            placeholder="Select Values"
                            options={valuesOptions}
                            selectProps={{showSearch: true}}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Events"
                        tooltip="Choose the events you registered via trackers"
                        name="events"
                    >
                        <EventReportSelector
                            value={selectedEventColumns}
                            onChange={setSelectedEventColumns}
                            placeholder="Select Events"
                            multiple
                        />
                    </Form.Item>
                </>
            )}
            <ReportFiltersComponent
                form={form}
                allFilters={allFilters}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                tooltip={filtersTooltip}
            />

            <Form.Item label=" " style={{marginBottom: 0}}>
                <Button
                    type="primary"
                    htmlType="submit"
                    style={{width: '160px', height: '36px'}}
                    disabled={loading}
                    aria-label="Create report"
                >
                    Create report
                </Button>
            </Form.Item>
        </AnchorsForm>
    )
}
